// Phase APP-4 · Lead "+ Request Packer" modal.
// 6 fields per APP-1 PackerRequestPayload. Submit disabled until valid.
// micro_hub_id is auto-set from user.scope.micro_hub_id and shown as
// a read-only chip. capability defaults to ["packing"] (also read-only).
// POST /api/micro-hub/workers/request
import { useEffect, useState } from "react";
import { X, Loader2, UserPlus, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import useAuthStore from "../../../store/authStore";
import api from "../../../services/api";

const ORANGE = "#FF6B00";
const SHIFTS = ["morning", "evening", "night", "flexible"];

export default function RequestPackerModal({ open, onClose, onDone }) {
  const user = useAuthStore((s) => s.user);
  const microHubId = user?.scope?.micro_hub_id || user?.hub_id;
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    shift_timing: "morning", remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ full_name: "", phone: "", email: "",
                  shift_timing: "morning", remarks: "" });
      setBusy(false); setErr("");
    }
  }, [open]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = form.full_name.trim().length >= 3
                && form.full_name.trim().length <= 80
                && /^\d{10}$/.test(form.phone.trim())
                && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
                && SHIFTS.includes(form.shift_timing)
                && !!microHubId
                && (form.remarks || "").length <= 500;

  const submit = async () => {
    if (!valid) return;
    setBusy(true); setErr("");
    try {
      const body = {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        micro_hub_id: microHubId,
        shift_timing: form.shift_timing,
        capability: ["packing"],
        remarks: form.remarks.trim(),
      };
      const r = await api.post("/micro-hub/workers/request", body);
      toast.success(`Packer requested · ${r.data.request_id}`);
      onDone?.(r.data?.request_id);
      onClose?.();
    } catch (e2) {
      const detail = e2?.response?.data?.detail || e2?.message || "Failed";
      setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
    }
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget && !busy) onClose?.(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-2"
         onClick={onBackdrop}
         data-testid="request-packer-modal"
         role="dialog" aria-modal="true">
      <div className="bg-white w-full sm:max-w-lg rounded-2xl shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <UserPlus className="h-4 w-4" style={{ color: ORANGE }}/>
          <h3 className="text-base font-semibold flex-1">Request a Packer</h3>
          <button onClick={onClose} aria-label="Close"
                  data-testid="request-packer-close"
                  className="p-1.5 rounded-full hover:bg-slate-100" disabled={busy}>
            <X className="h-4 w-4"/>
          </button>
        </div>
        <div className="px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
          {err && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700 inline-flex items-start gap-1.5"
                 data-testid="request-packer-error">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0"/> {err}
            </div>
          )}
          {/* Auto-bound micro_hub_id as a read-only chip */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs flex items-center gap-2"
               data-testid="request-packer-hub-chip">
            <span className="text-slate-500 uppercase tracking-wider">Micro hub</span>
            <span className="font-mono text-slate-800">{microHubId || "—"}</span>
          </div>
          <Field label="Full name *" hint="3–80 chars">
            <input value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                   data-testid="request-packer-name"
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Phone *" hint="10 digits">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                     maxLength={10} data-testid="request-packer-phone"
                     className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"/>
            </Field>
            <Field label="Shift *">
              <select value={form.shift_timing}
                      onChange={(e) => set("shift_timing", e.target.value)}
                      data-testid="request-packer-shift"
                      className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
                {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Email *">
            <input value={form.email} onChange={(e) => set("email", e.target.value)}
                   data-testid="request-packer-email"
                   className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
          </Field>
          {/* capability defaults to ["packing"], read-only */}
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Capability <span className="ml-2 font-mono text-slate-700 normal-case">[ packing ]</span>
            <span className="ml-2 text-slate-400 normal-case">(read-only)</span>
          </div>
          <Field label="Remarks" hint={`${(form.remarks || "").length}/500`}>
            <textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)}
                      rows={2} maxLength={500} data-testid="request-packer-remarks"
                      className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
          </Field>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  data-testid="request-packer-cancel"
                  className="text-sm px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={submit}
                  disabled={!valid || busy}
                  data-testid="request-packer-submit"
                  data-can-submit={valid && !busy ? "true" : "false"}
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  style={{ backgroundColor: ORANGE }}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin"/>}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-baseline justify-between">
        <span>{label}</span>
        {hint && <span className="normal-case text-slate-400">{hint}</span>}
      </label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
